import { useEffect, useState } from "react";
import { History, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { deDateTime } from "../../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „Checklisten-Audit sichtbar in Buchungsdetail"
 * (1 Sep 2026). Zeigt eine chronologische Liste aller Admin-Änderungen
 * an der Checkliste dieser Buchung — Name, Zeitpunkt, Aktion. Standard-
 * mäßig eingeklappt; öffnet sich per Klick auf den Header.
 */
const ACTION_LABEL = {
  "checklist-toggle": (r) => (r.done ? "Angehakt" : "Zurückgesetzt"),
  "service-toggle":   (r) => (r.done ? "Als erledigt markiert" : "Wieder offen gesetzt"),
  "service-add":      () => "Extra-Service hinzugefügt",
  "service-remove":   () => "Extra-Service entfernt",
  "std-disable":      () => "Inklusiv-Leistung deaktiviert",
  "std-restore":      () => "Inklusiv-Leistung reaktiviert",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return deDateTime(iso);
  } catch { return iso; }
}

export function BookingChecklistAuditPanel({ bookingId, refreshToken }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    let mounted = true;
    api.get(`/bookings/${bookingId}/checklist-audit`)
      .then((r) => { if (mounted) setRows(r.data || []); })
      .catch(() => { if (mounted) setRows([]); });
    return () => { mounted = false; };
  }, [bookingId, refreshToken]);

  const count = rows?.length ?? 0;
  return (
    <div className="mt-4" data-testid="booking-checklist-audit-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open ? "true" : "false"}
        data-testid="booking-checklist-audit-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-navy/10 bg-navy/[0.02] hover:bg-navy/5 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
          <div>
            <div className="label-caps text-navy-dark/75">Admin-Änderungen an der Checkliste</div>
            <div className="text-[11.5px] text-navy-dark/55 mt-0.5">
              {count === 0
                ? "Noch keine Admin-Änderungen protokolliert."
                : `${count} Änderung${count === 1 ? "" : "en"} · chronologisch, neueste zuerst`}
            </div>
          </div>
        </div>
        <ChevronDown
          className="w-4 h-4 text-navy-dark/50 transition-transform shrink-0"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={1.6}
        />
      </button>
      {open && rows && rows.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="booking-checklist-audit-list">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-start gap-3 p-3 bg-white border border-navy/10 text-[12.5px]"
              data-testid={`booking-checklist-audit-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-navy">
                  <span className="font-medium">{r.actor_name || "Admin"}</span>
                  <span className="text-navy-dark/50"> · </span>
                  <span className="text-navy-dark/75">{(ACTION_LABEL[r.action] || (() => r.action))(r)}</span>
                </div>
                <div className="text-[11.5px] text-navy-dark/60 mt-0.5">
                  {r.item_label || r.item_key || ""} <span className="text-navy-dark/40">({r.item_kind})</span>
                </div>
              </div>
              <div className="text-[11px] text-navy-dark/55 shrink-0 tabular-nums">
                {formatDate(r.at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
